import { useMapStateStore } from '../core'
import type { MapActionFailure, RequestedMapAction, Resolution } from './actions'
import { applyTransition } from './apply'
import type { MapCatalog } from './catalog'
import { resolve } from './resolve'

/**
 * What became of one request. `Superseded` is neither outcome the user can see:
 * a newer request started before this one finished resolving, so it was dropped
 * without touching the store and there is nothing to report about it.
 */
export type ResolutionOutcome =
  | { kind: 'Landed' }
  | { kind: 'Superseded' }
  | { kind: 'Failed'; failure: MapActionFailure }

/**
 * How many requests have been made, so that each one can tell whether it is
 * still the current one when its lookups come back.
 *
 * One counter shared by every caller, because they all commit to the one store.
 * Were an older request allowed to land, a slow lookup would win by finishing
 * last and leave the map somewhere the user had already moved on from.
 */
let requests = 0

/**
 * Resolve a request against live data, commit what it resolves to, and say what
 * became of it. A link the user arrived on and a control they clicked differ only
 * in who reports the outcome, so both come through here.
 */
export const runResolution = async (
  action: RequestedMapAction,
  catalog: MapCatalog
): Promise<ResolutionOutcome> => {
  const request = ++requests
  const attempted = await attempt(action, catalog)

  if (request !== requests) return { kind: 'Superseded' }
  if (attempted.kind === 'Threw') {
    return { kind: 'Failed', failure: { kind: 'LookupFailed', error: attempted.error } }
  }

  const { resolution } = attempted
  if (resolution.kind === 'Unresolvable') {
    return { kind: 'Failed', failure: { kind: 'Unresolvable', reason: resolution.reason } }
  }

  if (resolution.kind === 'Commit') {
    // Read the state again rather than reusing the one resolved against: a
    // resolution awaits the catalog, and the reducer derives its cascades from
    // whatever it is handed, so committing against the newer state is both
    // safe and closer to what the user last saw.
    useMapStateStore.setState(applyTransition(useMapStateStore.getState(), resolution.action))
  }

  return { kind: 'Landed' }
}

/**
 * Warm the cache for a request the user is likely about to make. `resolve`
 * commits nothing and reads only through the query cache, so a dry run fetches
 * exactly the lookups the real resolution would otherwise wait on. Best-effort:
 * an error here surfaces when the request is actually made, not before.
 */
export const prefetchResolution = (action: RequestedMapAction, catalog: MapCatalog): void => {
  void resolve(useMapStateStore.getState(), action, catalog).catch(() => undefined)
}

type Attempt = { kind: 'Resolved'; resolution: Resolution } | { kind: 'Threw'; error: Error }

/**
 * Resolving, with a lookup that threw carried back as a value, so that the
 * caller reaches both outcomes at the same place — after it has checked that its
 * request is still the current one.
 */
const attempt = async (action: RequestedMapAction, catalog: MapCatalog): Promise<Attempt> => {
  try {
    return {
      kind: 'Resolved',
      resolution: await resolve(useMapStateStore.getState(), action, catalog)
    }
  } catch (error: unknown) {
    return { kind: 'Threw', error: error instanceof Error ? error : new Error(String(error)) }
  }
}
